import moment from 'moment';

export const priorityToText = (priority: any): string => {
  const map: Record<number, string> = { 0: 'Low', 1: 'Medium', 2: 'High', 3: 'Urgent' };
  if (priority === undefined || priority === null || priority === '') return '';
  return typeof priority === 'number' ? map[priority] ?? String(priority) : String(priority);
};

export const stripHtml = (value: any): string => {
  if (!value) return '';
  try {
    return String(value)
      .replace(/<[^>]*>/g, '')
      .trim();
  } catch (e) {
    return String(value);
  }
};

export const truncateText = (text: any, max = 100): string => {
  const s = String(text || '');
  return s.length > max ? `${s.slice(0, max)}...` : s;
};

export const normalizeProjectStatus = (status: string | null | undefined): string => {
  if (!status) return 'New';
  const lower = status.toLowerCase();
  if (lower.includes('planning')) return 'Planning';
  if (lower.includes('review')) return 'Review';
  if (lower.includes('progress')) return 'In Progress';
  if (lower.includes('hold')) return 'On Hold';
  if (lower.includes('backlog')) return 'New';
  if (lower.includes('pending') || lower.includes('to do') || lower === 'todo') {
    return 'New';
  }
  if (lower.includes('completed') || lower.includes('done')) return 'Completed';
  return 'New';
};

export const mapStatusToColumnId = (status: string): string => {
  const normalized = normalizeProjectStatus(status);
  const map: Record<string, string> = {
    New: 'col-1',
    'On Hold': 'col-2',
    'In Progress': 'col-3',
    Completed: 'col-4',
  };
  return map[normalized] || 'col-1';
};

export const getLatestCompletionId = (task: any): any => {
  const completion = task?.latestCompletion;
  if (!completion) return undefined;
  return (
    completion.id ??
    completion._id ??
    completion.taskCompletionId ??
    completion.task_completion_id ??
    completion.completionId ??
    completion.uuid ??
    completion.guid
  );
};

export const passesName = (task: any, nameQuery: string): boolean => {
  if (!nameQuery) return true;
  return (task?.name || '').toLowerCase().includes(String(nameQuery).toLowerCase());
};

export const passesDetails = (task: any, detailsQuery: string): boolean => {
  if (!detailsQuery) return true;
  return stripHtml(task?.description || '')
    .toLowerCase()
    .includes(String(detailsQuery).toLowerCase());
};

export const passesDate = (task: any, dateQuery: any): boolean => {
  if (!dateQuery) return true;
  const taskDay = task?.start ? moment(task.start).startOf('day') : null;
  const selectedDay = moment(dateQuery).startOf('day');
  return !!taskDay && taskDay.isSame(selectedDay, 'day');
};

export const passesCreatedDate = (task: any, createdDateQuery: any): boolean => {
  if (!createdDateQuery) return true;
  const createdDay = task?.createdAt ? moment(task.createdAt).startOf('day') : null;
  const selectedDay = moment(createdDateQuery).startOf('day');
  return !!createdDay && createdDay.isSame(selectedDay, 'day');
};

export const passesPriority = (task: any, selected: string[]): boolean => {
  if (!Array.isArray(selected) || selected.length === 0) return true;
  const p = priorityToText(task?.priority);
  return selected.includes(p);
};

export const passesStatus = (statusText: string, selected: string[]): boolean => {
  if (!Array.isArray(selected) || selected.length === 0) return true;
  return selected.includes(statusText);
};

export const passesLocation = (locationText: string, selected: string[]): boolean => {
  if (!Array.isArray(selected) || selected.length === 0) return true;
  return selected.includes(locationText);
};

export const passesMember = (task: any, selected: any[]): boolean => {
  if (!Array.isArray(selected) || selected.length === 0) return true;
  const hasUnassigned = selected.includes('Unassigned');
  if (hasUnassigned && !task?.employeeId) return true;
  return selected.map(String).includes(String(task?.employeeId || ''));
};

export const passesPrefix = (task: any, selectedIds: string[]): boolean => {
  if (!Array.isArray(selectedIds) || selectedIds.length === 0) return true;
  return selectedIds.includes(String(task?.prefixId || ''));
};

export const mapDisplayStatusToApiStatus = (status: string): string => {
  if (!status) return 'Pending';
  const normalized = status.trim().toLowerCase();
  if (normalized === 'to do') return 'Pending';
  if (normalized === 'in progress') return 'InProgress';
  if (normalized === 'planning') return 'Planning';
  if (normalized === 'review') return 'Review';
  if (normalized === 'on hold') return 'OnHold';
  if (normalized === 'new') return 'Pending';
  if (normalized === 'completed') return 'Completed';
  if (normalized === 'snoozed') return 'Snoozed';
  if (normalized === 'expired') return 'Expired';
  return status;
};
